import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Package,
  Search,
  MapPin,
  RefreshCw,
  Filter,
  ArrowRight,
  Truck,
  CheckCircle,
  AlertTriangle,
  FileText,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const statusColors = {
  registered: 'bg-blue-100 text-blue-800',
  checked_in: 'bg-yellow-100 text-yellow-800',
  loaded: 'bg-violet-100 text-violet-800',
  in_transit: 'bg-amber-100 text-amber-800',
  offloaded: 'bg-purple-100 text-purple-800',
  claimed: 'bg-emerald-100 text-emerald-800',
  delivered: 'bg-emerald-100 text-emerald-800',
  lost: 'bg-red-100 text-red-800',
  found: 'bg-cyan-100 text-cyan-800',
  cancelled: 'bg-slate-200 text-slate-800',
  past_destination: 'bg-red-100 text-red-800',
};

const LuggageDashboardPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDestination, setSelectedDestination] = useState('all');

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    if (!token) return;

    setLoading(true);
    try {
      const [luggageRes, stationsRes] = await Promise.all([
        axios.get(`${API_URL}/luggage`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/stations`),
      ]);

      setItems(luggageRes.data || []);
      setStations(stationsRes.data || []);
    } catch (error) {
      console.error('Failed to load luggage dashboard data:', error);
      toast.error('Failed to load luggage dashboard');
    } finally {
      setLoading(false);
    }
  };

  const stationMap = useMemo(() => {
    return Object.fromEntries((stations || []).map((station) => [station.id, station]));
  }, [stations]);

  const destinationGroups = useMemo(() => {
    const groups = {};

    for (const item of items || []) {
      const city = getDestinationCity(item, stationMap);
      if (!city) continue;
      if (!groups[city]) groups[city] = [];
      groups[city].push(item);
    }

    return Object.entries(groups)
      .map(([city, cityItems]) => ({
        city,
        count: cityItems.length,
        itemCount: cityItems.length,
      }))
      .sort((a, b) => b.count - a.count || a.city.localeCompare(b.city));
  }, [items, stationMap]);

  const filteredItems = useMemo(() => {
    return (items || []).filter((item) => {
      const q = search.trim().toLowerCase();
      const destLabel = getDestinationLabel(item, stationMap);
      const destinationCity = getDestinationCity(item, stationMap);
      const matchesSearch =
        !q ||
        item.luggage_ref?.toLowerCase().includes(q) ||
        item.sender_name?.toLowerCase().includes(q) ||
        item.receiver_name?.toLowerCase().includes(q) ||
        destLabel.toLowerCase().includes(q) ||
        destinationCity.toLowerCase().includes(q);

      const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
      const matchesDestination = selectedDestination === 'all' || destinationCity === selectedDestination;

      return matchesSearch && matchesStatus && matchesDestination;
    });
  }, [items, search, statusFilter, selectedDestination, stationMap]);

  const totals = useMemo(() => {
    return {
      total: filteredItems.length,
      inTransit: filteredItems.filter((item) => ['registered', 'checked_in', 'loaded', 'in_transit', 'offloaded'].includes(item.status)).length,
      delivered: filteredItems.filter((item) => ['claimed', 'delivered'].includes(item.status)).length,
      pastDestination: filteredItems.filter((item) => item.past_destination || item.status === 'past_destination').length,
    };
  }, [filteredItems]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Luggage Dashboard</h1>
            <p className="text-slate-600">All luggage records with destination location visibility</p>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate('/dashboard/luggage-history')}
            >
              <FileText className="mr-2 h-4 w-4" />
              History View
            </Button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total</p>
                  <p className="text-2xl font-bold">{totals.total}</p>
                </div>
                <div className="rounded-full bg-blue-100 p-2 text-blue-700">
                  <Package className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">In transit</p>
                  <p className="text-2xl font-bold">{totals.inTransit}</p>
                </div>
                <div className="rounded-full bg-amber-100 p-2 text-amber-700">
                  <Truck className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Delivered</p>
                  <p className="text-2xl font-bold">{totals.delivered}</p>
                </div>
                <div className="rounded-full bg-emerald-100 p-2 text-emerald-700">
                  <CheckCircle className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-red-200 bg-red-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-700">Past destination</p>
                  <p className="text-2xl font-bold text-red-700">{totals.pastDestination}</p>
                </div>
                <div className="rounded-full bg-red-100 p-2 text-red-700">
                  <AlertTriangle className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="p-4">
            <div className="grid gap-3 md:grid-cols-[1.5fr_0.8fr]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  className="pl-10"
                  placeholder="Search by ref, sender, receiver or destination"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="relative">
                <Filter className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <select
                  className="h-10 w-full appearance-none rounded-md border border-slate-200 bg-white pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-panashe-blue"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All statuses</option>
                  <option value="registered">Registered</option>
                  <option value="checked_in">Checked in</option>
                  <option value="loaded">Loaded</option>
                  <option value="in_transit">In transit</option>
                  <option value="offloaded">Offloaded</option>
                  <option value="claimed">Claimed</option>
                  <option value="delivered">Delivered</option>
                  <option value="past_destination">Past destination</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {destinationGroups.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-800">Destination cities</h2>
              {selectedDestination !== 'all' && (
                <Button variant="outline" size="sm" onClick={() => setSelectedDestination('all')}>
                  Show all destinations
                </Button>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {destinationGroups.map((group) => {
                const isSelected = selectedDestination === group.city;

                return (
                  <button
                    key={group.city}
                    type="button"
                    onClick={() => setSelectedDestination(group.city)}
                    className={`rounded-xl border p-4 text-left transition-all ${
                      isSelected
                        ? 'border-panashe-blue bg-panashe-blue text-white shadow-lg'
                        : 'border-slate-200 bg-white text-slate-800 hover:border-panashe-blue hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <MapPin className={`h-4 w-4 ${isSelected ? 'text-white' : 'text-panashe-blue'}`} />
                        <span className="font-semibold">{group.city}</span>
                      </div>
                      <span className={`rounded-full px-2 py-1 text-xs font-semibold ${isSelected ? 'bg-white/15 text-white' : 'bg-slate-100 text-slate-700'}`}>
                        {group.count}
                      </span>
                    </div>
                    <p className={`mt-3 text-sm ${isSelected ? 'text-white/80' : 'text-slate-500'}`}>
                      {group.count === 1 ? 'luggage item' : 'luggage items'}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <Card>
          <CardHeader className="border-b">
            <CardTitle className="font-heading flex items-center gap-2">
              <MapPin className="h-5 w-5 text-panashe-blue" />
              {selectedDestination === 'all' ? 'Destination Overview' : `${selectedDestination} luggage`} 
            </CardTitle>
          </CardHeader>

          <CardContent className="p-0">
            {loading ? (
              <div className="p-10 text-center text-slate-500">
                <RefreshCw className="mx-auto mb-2 h-8 w-8 animate-spin text-panashe-blue" />
                Loading luggage dashboard...
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="p-10 text-center text-slate-500">
                <Package className="mx-auto mb-3 h-12 w-12 text-slate-300" />
                No luggage items match the current filter.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-xs font-medium uppercase tracking-wide text-slate-500">Ref</th>
                      <th className="px-4 py-3 text-xs font-medium uppercase tracking-wide text-slate-500">Sender</th>
                      <th className="px-4 py-3 text-xs font-medium uppercase tracking-wide text-slate-500">Destination</th>
                      <th className="px-4 py-3 text-xs font-medium uppercase tracking-wide text-slate-500">Status</th>
                      <th className="px-4 py-3 text-xs font-medium uppercase tracking-wide text-slate-500">Updated</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {filteredItems.map((item) => {
                      const destinationLabel = getDestinationLabel(item, stationMap);
                      const updatedAt = item.updated_at || item.created_at;

                      return (
                        <tr key={item.id || item.luggage_ref} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => navigate(`/luggage-ticket/${item.luggage_ref}`)}
                              className="font-mono font-bold text-panashe-blue hover:underline"
                            >
                              {item.luggage_ref}
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-slate-800">{item.sender_name || 'Unknown sender'}</div>
                            <div className="text-xs text-slate-500">{item.sender_phone || '-'}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                              <MapPin className="h-4 w-4 text-slate-400" />
                              {destinationLabel}
                            </div>
                            {item.route_name && <div className="mt-1 text-xs text-slate-500">{item.route_name}</div>}
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={statusColors[item.status] || 'bg-slate-100 text-slate-800'}>
                              {String(item.status || 'registered').replace(/_/g, ' ')}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {updatedAt ? format(parseISO(updatedAt), 'dd MMM yyyy HH:mm') : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

function getDestinationLabel(item, stations) {
  const specificDest = item.custom_destination || item.dropoff_point || item.destination;
  if (specificDest && specificDest.trim()) {
    return specificDest.trim();
  }

  if (item.destination_station_id && stations) {
    const station = stations[item.destination_station_id];
    if (station) {
      return `${station.name || station.city || 'Station'}${station.city ? ` (${station.city})` : ''}`;
    }
  }

  return 'Unassigned destination';
}

function getDestinationCity(item, stations) {
  const specificDest = item.custom_destination || item.dropoff_point || item.destination;
  if (specificDest && specificDest.trim()) {
    return specificDest.trim();
  }

  if (item.destination_station_id && stations?.[item.destination_station_id]?.city) {
    return stations[item.destination_station_id].city;
  }

  const label = getDestinationLabel(item, stations);
  return label || 'Unassigned';
}

export default LuggageDashboardPage;
